import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useMutation } from '@tanstack/react-query';
import Modal from '../PackageBuilders/MultiPose/Modal';
import reviewStyles from '../PackageBuilders/MultiPose/ReviewModal.module.scss';
import Button from '../Button/v2';
import { useCustomer } from '../../contexts/CustomerContext';
import { Cart } from '../../models';
import { isEmailValid } from '../../utils/functions';
import styles from './EmailInterceptor.module.scss';

const EmailInterceptorContext = createContext({});

export function EmailInterceptorProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const callback = useRef(null);
  const { customer, setCustomer } = useCustomer();

  // Add this useEffect to extract email from URL when component mounts
  useEffect(() => {
    const extractEmailFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const emailFromUrl = urlParams.get('email');
      
      if (emailFromUrl && isEmailValid(emailFromUrl) && customer && !customer?.cart?.email) {
        // If we have a valid email in the URL and customer doesn't have an email yet
        const updateCart = async () => {
          try {
            const cart = new Cart(customer?.cart ? { ...customer.cart } : {});
            cart.isPersisted = customer?.cart?.id?.length > 0;
            cart.email = emailFromUrl;
            
            await cart.save();
            
            setCustomer({ ...customer, cart, email: emailFromUrl });
          } catch (error) {
            console.error("Failed to update cart with email from URL:", error);
          }
        };
        
        updateCart();
      }
    };
    
    extractEmailFromUrl();
  }, [customer, setCustomer]);

  const intercept = (newCallback) => {
    if (customer && !customer?.cart?.email) setIsOpen(true);

    if (customer && customer?.cart?.email) {
      newCallback();
      return;
    }

    callback.current = newCallback;
  };

  useEffect(() => {
    if (isOpen) return;
    if (!callback.current) return;

    callback.current();
    callback.current = undefined;
  }, [isOpen, callback.current]);

  return (
    <EmailInterceptorContext.Provider value={{ isOpen, setIsOpen, intercept }}>
      {children}
    </EmailInterceptorContext.Provider>
  );
}

export function useEmailInterceptor() {
  return useContext(EmailInterceptorContext);
}

function EmailInterceptor() {
  const { isOpen, setIsOpen } = useEmailInterceptor();
  const { customer, setCustomer } = useCustomer();
  const [email, setEmail] = useState('');

  const { isLoading, mutate } = useMutation({
    mutationKey: ['cart', 'add email'],
    mutationFn: async () => {
      if (customer && customer?.cart) {
        const cart = new Cart({ ...customer.cart });
        cart.isPersisted = true;
        cart.email = email;

        await cart.save();

        setCustomer({ ...customer, cart, email });
        setIsOpen(false);
      } else {
        const cart = new Cart({ email });

        await cart.save();

        cart.email = email;

        setCustomer({ ...customer, cart, email });
        setIsOpen(false);
      }
    },
  });

  return (
    <Modal.Content isOpen={isOpen}>
      <Modal.Title className={cn(reviewStyles.title, styles.title)}>Please Enter Your Email to Continue</Modal.Title>
      <input className={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
      <Button disabled={isLoading || !isEmailValid(email)} onClick={mutate}>Submit</Button>
    </Modal.Content>
  );
}

// Make sure to export EmailInterceptor as the default export
export default EmailInterceptor;

// Also export everything together for convenience
export { EmailInterceptor };
